import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const HomePage = () => {
  const services = [
    {
      icon: 'ri-user-heart-line',
      title: 'Individual Counseling',
      description: 'One-on-one sessions tailored to your unique journey, combining Islamic principles with proven recovery methods.',
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      icon: 'ri-team-line',
      title: 'Group Support',
      description: 'Connect with others on similar paths in a safe, confidential environment guided by faith and mutual support.',
      color: 'from-rose-500 to-rose-600',
    },
    {
      icon: 'ri-book-open-line',
      title: 'Islamic Guidance',
      description: 'Spiritual counseling rooted in Quranic teachings and Prophetic wisdom to strengthen your faith during recovery.',
      color: 'from-amber-500 to-amber-600',
    },
    {
      icon: 'ri-parent-line',
      title: 'Family Support',
      description: 'Help your loved ones understand and support your journey with family counseling and education programs.',
      color: 'from-purple-500 to-purple-600',
    },
  ];

  const faqs = [
    {
      question: 'Is your program specifically for Muslims?',
      answer: 'Yes, our program is designed specifically for Muslims, integrating Islamic principles, Quranic guidance, and Prophetic wisdom into evidence-based recovery methods.',
    },
    {
      question: 'How confidential is the counseling?',
      answer: 'Complete confidentiality is our priority. All sessions are private, and we maintain strict professional ethics in accordance with both Islamic values and counseling standards.',
    },
    {
      question: 'Do you offer online sessions?',
      answer: 'Yes, we offer both in-person and secure online sessions to accommodate your needs and ensure accessibility regardless of your location.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20 px-4">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6838dcabc788ed3122a3ed7f3b6be772.jpg"
            alt="New Beginnings"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/40"></div>
        </div>

        {/* Arabic Calligraphy Watermark - Positioned much higher */}
        <div className="absolute top-8 sm:top-12 left-1/2 -translate-x-1/2 lg:left-20 lg:translate-x-0 lg:top-16 text-white/10 text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-serif z-0">
          شفاء
        </div>

        {/* Hero Content - Much more top margin */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full mt-48 sm:mt-40 lg:mt-32">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text content - Order first on all devices */}
            <div className="text-center lg:text-left order-first">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-bold text-white leading-tight mb-4 sm:mb-6">
                Are you tired of relapsing and wearing a mask at the mosque, at home and at work?
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-teal-100 font-light mb-8 sm:mb-12 leading-relaxed">
                I help Muslim men struggling with Porn, Masturbation and Other addictions, heal the root of their addiction without willpower so they can remove the shame and become the man they were supposed to be
              </p>
              <div className="flex flex-col gap-4">
                <a
                  href="https://calendly.com/coachabdurrahman1/why-your-still-relapsing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-base sm:text-lg font-medium rounded-full hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl cursor-pointer text-center"
                >
                  <span className="text-center">Book your free addiction assessment and get a 7 day action plan to not relapse</span>
                  <i className="ri-arrow-right-line ml-2 flex-shrink-0"></i>
                </a>
              </div>
            </div>

            {/* Image beside text - Order second on mobile, last on desktop */}
            <div className="flex justify-center lg:justify-end order-last">
              <div className="relative w-full max-w-sm sm:max-w-md">
                <div className="absolute -inset-4 bg-gradient-to-br from-teal-500/30 to-emerald-500/30 rounded-3xl blur-xl"></div>
                <img
                  src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/0daee8bc804b6e3c34e2122668dd651e.jpeg"
                  alt="Coach Abdurrahman"
                  className="relative w-full h-auto rounded-3xl shadow-2xl border-4 border-white/20"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Right Text */}
        <div className="absolute bottom-24 right-20 text-white/90 text-lg max-w-md text-right hidden xl:block">
          <p className="leading-relaxed">
            Compassionate, confidential support rooted in Islamic principles
          </p>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-50 to-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.05) 35px, rgba(0,0,0,.05) 70px)' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-rose-100 text-rose-700 rounded-full text-sm font-medium mb-6">
                The Hidden Struggle
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-gray-900 mb-4 leading-tight">
                You're Not Alone<br />In This Pain
              </h2>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-rose-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-loop-left-line text-rose-600 text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">You keep repenting — and keep falling.</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Each relapse makes you feel further from Allah and doubt whether your tawbah even counts.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-user-forbid-line text-amber-600 text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">You live a double life.</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Outwardly practicing, inwardly trapped — carrying the constant fear of hypocrisy and exposure.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-purple-100 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-heart-crack-line text-purple-600 text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Your worship feels empty.</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Salah is mechanical, du'a feels blocked, and your days pass with no barakah or focus.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-slate-200 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-shield-cross-line text-slate-700 text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Shame is poisoning your family and future.</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Even unseen, your addiction betrays the people you love — and the man you're meant to be.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full h-[500px] sm:h-[600px] lg:h-[700px] mt-8 lg:mt-0">
              <img
                src="https://readdy.ai/api/search-image?query=Somber%20portrait%20of%20middle%20eastern%20muslim%20man%20with%20beard%20sitting%20alone%20in%20dim%20lighting%20looking%20down%20with%20expression%20of%20deep%20sadness%20and%20internal%20struggle%20wearing%20simple%20clothing%20against%20plain%20dark%20muted%20background%20conveying%20emotional%20weight%20and%20isolation%20from%20addiction&width=800&height=1000&seq=pain1v3&orientation=portrait"
                alt="Struggling with addiction"
                className="w-full h-full object-cover object-top rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dream Section - What If Instead */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 lg:mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <i className="ri-lightbulb-line"></i>
              <span>The Transformation</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-gray-900 mb-4">
              What If, Instead...
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-shield-check-line text-emerald-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">There are no more secrets to carry.</h3>
                  <p className="text-gray-700 leading-relaxed">
                    No lies. No hidden habits pulling you in two directions.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-heart-add-line text-teal-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">The guilt and shame lift.</h3>
                  <p className="text-gray-700 leading-relaxed">
                    That constant feeling of hypocrisy finally fades.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-hand-heart-line text-blue-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Your salah feels real again.</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You stand before Allah with honesty — not perfection, just truth.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-sparkling-line text-amber-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Your days regain barakah.</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You're focused, productive, and present — with time that finally feels meaningful.
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full h-[500px] sm:h-[600px] lg:h-[700px] mt-8 lg:mt-0 order-first lg:order-last">
              <img
                src="https://readdy.ai/api/search-image?query=Peaceful%20muslim%20man%20with%20beard%20in%20prayer%20position%20wearing%20white%20thobe%20in%20bright%20sunlit%20mosque%20with%20serene%20expression%20showing%20spiritual%20peace%20and%20contentment%20warm%20golden%20natural%20lighting%20clean%20simple%20background%20conveying%20hope%20and%20renewal&width=800&height=1000&seq=dream1v3&orientation=portrait"
                alt="Peace and spiritual renewal"
                className="w-full h-full object-cover object-top rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-3xl opacity-20 blur-xl"></div>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/b849fd7eb7ea2cb067ce318d0a25c324.png"
                    alt="Coach Abdurrahman"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-6">
                About Your Coach
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6 lg:mb-8 leading-tight">
                Real Experience. Real Results.
              </h2>
              <div className="space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                <p className="first-letter:text-4xl sm:first-letter:text-6xl first-letter:font-serif first-letter:font-bold first-letter:text-teal-600 first-letter:float-left first-letter:mr-3 first-letter:leading-none">
                  I'm a 6‑year addiction coach and former teacher, having guided 150+ Muslim men 1‑to‑1 — anonymously — to break free from addiction. I've spent over 2,000 hours mentoring on mindset, habits, and frameworks for real success.
                </p>
                <p>
                  Avid jiu-jitsu practitioner, proud father of two, and passionate solopreneur, I help men reclaim their lives with focus, clarity, and integrity.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-teal-600 mb-2">150+</div>
                  <div className="text-xs sm:text-sm text-gray-600">Men Coached</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-teal-600 mb-2">2,000+</div>
                  <div className="text-xs sm:text-sm text-gray-600">Mentoring Hours</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-teal-600 mb-2">6</div>
                  <div className="text-xs sm:text-sm text-gray-600">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 lg:mb-16">
            <div className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-4">
              The Solution
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-white mb-4 leading-tight">
              Here's How We'll<br />Change Your Life…
            </h2>
            <p className="text-xl sm:text-2xl text-teal-100 font-semibold mb-4">
              The Freedom From Addiction Program
            </p>
            <p className="text-lg sm:text-xl text-teal-200 max-w-3xl mx-auto">
              Work with me 1:1 and finally break free from the cycle of guilt, shame, and relapse.
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-12">
            <p className="text-base sm:text-lg text-teal-100 mb-8 text-center">
              We'll cover everything you need to reclaim control — from why you've been stuck, to the exact steps that will get you moving forward, and how to build lasting sobriety and clarity in your life.
            </p>
            <p className="text-lg sm:text-xl text-teal-100 font-semibold mb-8 text-center">
              Get full, tailored 1:1 support to:
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-xl sm:text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">Stop relying on willpower alone</h3>
                    <p className="text-gray-700 text-sm sm:text-base">Get a clear roadmap out of addiction with step-by-step guidance.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                    <i className="ri-calendar-check-line text-xl sm:text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">Build daily habits that work</h3>
                    <p className="text-gray-700 text-sm sm:text-base">Take action every day, one step at a time, without guesswork.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                    <i className="ri-heart-pulse-line text-xl sm:text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">Master your emotions and triggers</h3>
                    <p className="text-gray-700 text-sm sm:text-base">Learn how to process, sit with, and manage the feelings that lead you back to addiction.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                    <i className="ri-home-heart-line text-xl sm:text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">Design your environment for success</h3>
                    <p className="text-gray-700 text-sm sm:text-base">At home, at work, everywhere, so staying sober becomes natural.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-lg sm:col-span-2">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                    <i className="ri-brain-line text-xl sm:text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">Upgrade your mindset</h3>
                    <p className="text-gray-700 text-sm sm:text-base">Break free from limiting patterns like black-and-white thinking and finally think in ways that support your recovery.</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-base sm:text-lg text-teal-100 mb-10 text-center">
              With 1:1 coaching, you'll gain clarity, confidence, and control, so every day feels purposeful. No more hiding, no more shame — just consistent progress toward a life you can be proud of.
            </p>

            <div className="text-center">
              <a
                href="https://calendly.com/coachabdurrahman1/why-your-still-relapsing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-teal-700 px-8 sm:px-10 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap"
              >
                Book Your Free Addiction Assessment and Get a 7 Day Action Plan to Not Relapse
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Clients Have Been Saying
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Real conversations, real transformations from men who've walked this path
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* First WhatsApp Screenshot */}
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/beab011d22fa7a891c8a702296146923.png"
                alt="Client Testimonial"
                className="w-full h-auto"
              />
            </div>

            {/* Second WhatsApp Screenshot */}
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/7b4914e47e3339416dddaf200dd23efb.png"
                alt="Client Testimonial"
                className="w-full h-auto"
              />
            </div>
          </div>

          <div className="text-center mt-12 lg:mt-16">
            <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              These are real conversations from real men who decided to take action. Your story could be next.
            </p>
            <a
              href="https://calendly.com/coachabdurrahman1/why-your-still-relapsing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-teal-600 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold hover:bg-teal-700 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap"
            >
              Start Your Transformation Today
            </a>
          </div>
        </div>
      </section>

      {/* 3 Steps Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-gray-900 mb-4">
              Freedom From Addiction<br />Is Just 3 Steps Away
            </h2>
          </div>

          {/* Steps */}
          <div className="space-y-12 lg:space-y-16 mb-16 lg:mb-20">
            {/* Step 1 */}
            <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-lg">
                  <span className="text-2xl sm:text-3xl font-bold text-white">1</span>
                </div>
              </div>
              <div className="flex-1 pt-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Book Your Private Call
                </h3>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  Start with a free, confidential strategy call.<br />
                  No judgment. No pressure. Just a clear conversation about where you are — and where you want to be.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg">
                  <span className="text-2xl sm:text-3xl font-bold text-white">2</span>
                </div>
              </div>
              <div className="flex-1 pt-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Understand Why You've Been Stuck
                </h3>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                  We identify exactly why willpower hasn't worked, what's been keeping you trapped in cycles of guilt and relapse, and the specific mindsets, habits, and triggers that have held you back.
                </p>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  You'll finally understand what needs to change — and what doesn't.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl shadow-lg">
                  <span className="text-2xl sm:text-3xl font-bold text-white">3</span>
                </div>
              </div>
              <div className="flex-1 pt-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Build a Clear Path Out
                </h3>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                  Together, we create a simple, structured plan: daily habits, emotional tools, environmental changes, and mindset upgrades that lead to real, lasting freedom.
                </p>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  No guessing. No starting over. Just steady progress, clarity, and barakah in your days.
                </p>
              </div>
            </div>
          </div>

          {/* Ready to Break Free */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold mb-6">
              Ready to Break Free?
            </h3>
            <p className="text-lg sm:text-xl text-teal-50 mb-6 leading-relaxed max-w-3xl mx-auto">
              You already know the cycle of guilt, shame, and relapse — and you're done letting it run your life.
            </p>
            <p className="text-base sm:text-lg text-teal-100 mb-6 leading-relaxed max-w-3xl mx-auto">
              In 1:1 coaching, you'll get the exact steps, habits, and mindset shifts that have helped 150+ Muslim men leave addiction behind with confidence and integrity.
            </p>
            <p className="text-base sm:text-lg text-teal-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              I take on a limited number of clients so every man gets real support and real results.
            </p>
            <p className="text-lg sm:text-xl font-semibold text-white mb-10">
              Your next step isn't about willpower — it's about the plan that works.
            </p>
            <a
              href="https://calendly.com/coachabdurrahman1/why-your-still-relapsing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-teal-700 px-8 sm:px-12 py-4 sm:py-6 rounded-full text-base sm:text-lg font-bold hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap"
            >
              Book Your Free Addiction Assessment
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
